import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Database, Table, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';

export default function SchemaExplorer() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTables, setExpandedTables] = useState(new Set());
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    fetchSchema();
  }, []);

  const fetchSchema = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch ALL rows with pagination to handle large schemas
      let allData = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error: queryError, count } = await supabase
          .rpc('get_schema_info', {}, {
            count: 'exact',
            head: false
          })
          .range(from, from + pageSize - 1);

        if (queryError) {
          throw queryError;
        }

        if (data && data.length > 0) {
          allData = [...allData, ...data];
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      console.log(`Loaded ${allData.length} columns from schema`);
      const grouped = groupSchemaData(allData);
      console.log(`Grouped into ${grouped.length} tables`);
      setTables(grouped);
    } catch (err) {
      console.error('Error fetching schema:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const groupSchemaData = (data) => {
    if (!data) return [];

    const tableMap = new Map();

    data.forEach(row => {
      const tableName = row.table_name;

      if (!tableMap.has(tableName)) {
        tableMap.set(tableName, {
          name: tableName,
          description: row.table_description,
          estimatedRows: row.estimated_rows,
          tableSize: row.table_size,
          columns: []
        });
      }

      tableMap.get(tableName).columns.push({
        name: row.column_name,
        type: row.data_type,
        nullable: row.is_nullable === 'YES',
        default: row.column_default,
        position: row.ordinal_position
      });
    });

    // Convert to array and sort columns by position
    return Array.from(tableMap.values()).map(table => ({
      ...table,
      columns: table.columns.sort((a, b) => a.position - b.position)
    })).sort((a, b) => a.name.localeCompare(b.name));
  };

  const toggleTable = (tableName) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName);
    } else {
      newExpanded.add(tableName);
    }
    setExpandedTables(newExpanded);
  };

  const copyToClipboard = async (text, field) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const copyTableSchema = async (table) => {
    const schemaText = `Table: ${table.name}\n\n` +
      table.columns.map(col =>
        `${col.name.padEnd(30)} ${col.type.padEnd(20)} ${col.nullable ? 'NULL' : 'NOT NULL'}${col.default ? ` DEFAULT ${col.default}` : ''}`
      ).join('\n');

    await copyToClipboard(schemaText, `table-${table.name}`);
  };

  const filteredTables = tables.filter(table =>
    table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    table.columns.some(col => col.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-stone-600">Loading schema...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Schema</h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchSchema}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Database className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl font-bold text-stone-900">Database Schema Explorer</h1>
          </div>
          <p className="text-stone-600">
            Interactive view of all Supabase tables and columns
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
            <input
              type="text"
              placeholder="Search tables or columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-stone-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border-2 border-stone-300 rounded-lg p-4">
            <div className="text-sm font-semibold text-stone-600 mb-1">Total Tables</div>
            <div className="text-3xl font-bold text-indigo-600">{tables.length}</div>
          </div>
          <div className="bg-white border-2 border-stone-300 rounded-lg p-4">
            <div className="text-sm font-semibold text-stone-600 mb-1">Total Columns</div>
            <div className="text-3xl font-bold text-indigo-600">
              {tables.reduce((sum, table) => sum + table.columns.length, 0)}
            </div>
          </div>
          <div className="bg-white border-2 border-stone-300 rounded-lg p-4">
            <div className="text-sm font-semibold text-stone-600 mb-1">Filtered Results</div>
            <div className="text-3xl font-bold text-indigo-600">{filteredTables.length}</div>
          </div>
        </div>

        {/* Tables List */}
        <div className="space-y-4">
          {filteredTables.length === 0 ? (
            <div className="bg-white border-2 border-stone-300 rounded-lg p-8 text-center">
              <Table className="w-12 h-12 text-stone-400 mx-auto mb-3" />
              <p className="text-stone-600">No tables found matching "{searchTerm}"</p>
            </div>
          ) : (
            filteredTables.map(table => {
              const isExpanded = expandedTables.has(table.name);

              return (
                <div key={table.name} className="bg-white border-2 border-stone-300 rounded-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="px-6 py-4 flex items-start justify-between gap-4">
                    <button
                      onClick={() => toggleTable(table.name)}
                      className="flex items-center gap-3 hover:bg-stone-50 transition-colors flex-1 -mx-6 px-6 py-2"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-stone-600 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-stone-600 flex-shrink-0" />
                        )}
                        <Table className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                        <div className="flex flex-col items-start flex-1">
                          <span className="font-mono font-semibold text-lg text-stone-900">
                            {table.name}
                          </span>
                          {table.description && (
                            <span className="text-sm text-stone-600 mt-0.5">
                              {table.description}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm font-medium text-stone-600 flex-shrink-0">
                        {table.estimatedRows !== undefined && table.estimatedRows !== null && (
                          <span className="bg-stone-100 px-2.5 py-1 rounded-md whitespace-nowrap">
                            {table.estimatedRows.toLocaleString()} rows
                          </span>
                        )}
                        {table.tableSize && (
                          <span className="bg-stone-100 px-2.5 py-1 rounded-md whitespace-nowrap">
                            {table.tableSize}
                          </span>
                        )}
                        <span className="bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-md whitespace-nowrap">
                          {table.columns.length} columns
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={() => copyTableSchema(table)}
                      className="p-2 hover:bg-stone-100 rounded transition-colors flex items-center gap-2 text-sm font-medium text-stone-700 flex-shrink-0"
                      title="Copy full table schema"
                    >
                      {copiedField === `table-${table.name}` ? (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-green-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy Schema</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Columns Table */}
                  {isExpanded && (
                    <div className="border-t-2 border-stone-200">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-stone-100">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">
                                Column Name
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">
                                Data Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">
                                Nullable
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">
                                Default Value
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-200">
                            {table.columns.map(column => (
                              <tr key={column.name} className="hover:bg-stone-50">
                                <td className="px-6 py-4">
                                  <span className="font-mono text-sm text-stone-900 font-medium">
                                    {column.name}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800">
                                    {column.type}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  {column.nullable ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800">
                                      NULL
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800">
                                      NOT NULL
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  {column.default ? (
                                    <span className="font-mono text-xs text-stone-600">
                                      {column.default}
                                    </span>
                                  ) : (
                                    <span className="text-stone-400 text-xs">-</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <button
                                    onClick={() => copyToClipboard(column.name, `${table.name}.${column.name}`)}
                                    className="p-1.5 hover:bg-stone-200 rounded transition-colors"
                                    title="Copy column name"
                                  >
                                    {copiedField === `${table.name}.${column.name}` ? (
                                      <Check className="w-4 h-4 text-green-600" />
                                    ) : (
                                      <Copy className="w-4 h-4 text-stone-600" />
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
